import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import PaddedLoader from 'components/PaddedLoader';
import RatingEmoji from 'components/RatingEmoji';
import toast from 'helpers/toast';
import usePublicApi from 'public-api';
import { getErrors, isResponse } from 'public-api/utils';
import { useAsyncFunction } from 'stores/hooks';
import { Button, Form, Message, Segment } from 'ui';

type RouteProps = { companyId: UUIDField; shippingId: UUIDField; rating: '1' | '2' | '3' };

function CustomerPaidShippingRating() {
  const { companyId, shippingId, rating } = useParams<RouteProps>();
  const [ratingQuestions, setRatingQuestions] = useState();
  const [ratingAnswers, setRatingAnswers] = useState<BackendPublic.CustomerPaidShippingRatingRequest>();
  const [completed, setCompleted] = useState(false);
  const [errors, setErrors] = useState<null | BackendPublic.CustomerPaidShippingRatingRequest>();
  const api = usePublicApi();

  const load = async () => {
    const oldCPSRating = await api.TransportRatingApi.retrieve({
      company_id: companyId!,
      shipping_id: shippingId!,
    });
    if (oldCPSRating.shipping) {
      setCompleted(true);
    }

    const options = await api.TransportRatingApi.options({
      company_id: companyId!,
      shipping_id: shippingId!,
    });

    setRatingQuestions(options.actions.POST);

    setRatingAnswers({ rating_satisfaction: Number(rating), shipping: shippingId });
  };
  const [handleLoad, isLoading] = useAsyncFunction(load);

  const submit = async () => {
    try {
      await api.TransportShippingRatingApi.create({ request: ratingAnswers });
      setCompleted(true);
      toast.success(gettext('Thank you for your feedback.'), { title: gettext('Feedback successfully saved') });
    } catch (error) {
      if (isResponse(error) && error.response.status === 400) {
        setErrors(await getErrors(error));
        toast.errorBadRequest();
      } else {
        toast.error();
      }
    }
  };
  const [handleSubmit, isSubmitting] = useAsyncFunction(submit);

  useEffect(() => {
    handleLoad();
  }, []);

  if (!ratingQuestions || isLoading) {
    return <PaddedLoader />;
  }

  if (completed) {
    return (
      <div className="flex content-center justify-center">
        <Segment padded>
          <h4 style={{ padding: '0 5rem' }}>Děkujeme vám za hodnocení.</h4>
        </Segment>
      </div>
    );
  }

  return (
    <div className="flex justify-center">
      <Segment style={{ marginTop: '2rem', maxWidth: '650px' }}>
        <Form onSubmit={handleSubmit}>
          <Form.Fields>
            <Form.Field label="Byli jste spokojeni se službami dopravy?">
              <div className="flex items-center justify-start gap-2 pt-1">
                {[...Array(3).keys()].reverse().map((idx) => (
                  <div
                    onClick={() => setRatingAnswers((old) => ({ ...old, rating_satisfaction: idx }))}
                    role="button"
                    tabIndex={0}
                    style={{
                      opacity: ratingAnswers?.rating_satisfaction === idx ? 1 : 0.15,
                      cursor: 'pointer',
                    }}
                  >
                    <RatingEmoji rating={idx} />
                  </div>
                ))}
              </div>
            </Form.Field>

            <Form.Field error={errors?.rating_intuitivity} label="Byl pro vás proces objednání dopravy intuitivní?">
              <div className="space-y-1">
                {ratingQuestions.rating_intuitivity.choices.map((choice) => (
                  <div key={choice.value}>
                    <Form.Radio
                      name={choice.value}
                      label={choice.display_name}
                      value={Boolean(
                        ratingAnswers.rating_intuitivity && ratingAnswers.rating_intuitivity === choice.value,
                      )}
                      onChange={() => setRatingAnswers((old) => ({ ...old, rating_intuitivity: choice.value }))}
                    />
                  </div>
                ))}
              </div>
            </Form.Field>

            <Form.Field
              error={errors?.rating_shipping_provider_reason}
              label="Jaký byl důvod, že jste si zvolili tohoto dopravce?"
            >
              <div className="space-y-1">
                {ratingQuestions.rating_shipping_provider_reason.choices.map((choice) => (
                  <div key={choice.value}>
                    <Form.Radio
                      label={choice.display_name}
                      value={Boolean(
                        ratingAnswers.rating_shipping_provider_reason &&
                          ratingAnswers.rating_shipping_provider_reason === choice.value,
                      )}
                      onChange={() =>
                        setRatingAnswers((old) => ({ ...old, rating_shipping_provider_reason: choice.value }))
                      }
                    />
                  </div>
                ))}
              </div>
            </Form.Field>

            {ratingAnswers.rating_shipping_provider_reason &&
              ratingAnswers.rating_shipping_provider_reason === 'OTHER' && (
                <Form.Textarea
                  label="Jaký?"
                  error={errors?.shipping_provider_other_reason}
                  value={ratingAnswers.shipping_provider_other_reason}
                  onChange={(value) => setRatingAnswers((old) => ({ ...old, shipping_provider_other_reason: value }))}
                />
              )}

            <Form.Field error={errors?.rating_problems} label="Nastal nějaký problém s dopravou?">
              <div className="space-y-1">
                {ratingQuestions.rating_problems.choices.map((choice) => (
                  <div key={choice.value}>
                    <Form.Radio
                      label={choice.display_name}
                      value={Boolean(ratingAnswers.rating_problems && ratingAnswers.rating_problems === choice.value)}
                      onChange={() => setRatingAnswers((old) => ({ ...old, rating_problems: choice.value }))}
                    />
                  </div>
                ))}
              </div>
            </Form.Field>

            {ratingAnswers.rating_problems && ratingAnswers.rating_problems === 'YES' && (
              <Form.Textarea
                label="Jaký problém nastal?"
                error={errors?.problem_comment}
                value={ratingAnswers.problem_comment}
                onChange={(value) => setRatingAnswers((old) => ({ ...old, problem_comment: value }))}
              />
            )}
            {Boolean(errors) && <Message error>Doplňte prosím označená chybějící pole.</Message>}
            <div className="flex content-center justify-center">
              <Button submit primary loading={isSubmitting}>
                Odeslat hodnocení
              </Button>
            </div>
          </Form.Fields>
        </Form>
      </Segment>
    </div>
  );
}

export default CustomerPaidShippingRating;
